import { t } from '../../i18n/init.js';
import { formatBytes, formatPercent } from '../../lib/format.js';
import type { ComponentInstance } from '../component.js';
import { setHtmlIcon, setText } from '../dom-helpers.js';
import { ICON_SPINNER } from '../icons.js';

export interface LoadingProps {
  filename: string;
  clientName: string | null;
  modelProgress: number; // 0..1, 0 = indeterminate
  sizeBytes: number;
  embed: boolean;
}

export function mountLoading(
  root: HTMLElement,
  initial: LoadingProps,
): ComponentInstance<LoadingProps> {
  root.innerHTML = '';

  const panel = document.createElement('div');
  // Inline styles drive layout — Tailwind utility classes were placeholders
  // that the prebuild does not see for jsdom contexts and that contained
  // empty conditional tokens (DOMTokenList.add throws on empty strings).
  panel.classList.add('viewer-ui-interactive');
  panel.style.cssText = initial.embed
    ? 'position:fixed;bottom:1rem;right:1rem;background:rgba(15,15,17,0.85);backdrop-filter:blur(8px);color:#f4f4f5;padding:0.75rem 1rem;border-radius:0.5rem;max-width:24rem;font-family:system-ui,sans-serif;font-size:0.875rem;display:flex;align-items:center;gap:0.75rem;z-index:10;pointer-events:auto'
    : 'position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:rgba(9,9,11,0.92);color:#f4f4f5;font-family:system-ui,sans-serif;z-index:10;pointer-events:auto';

  const inner = document.createElement('div');
  inner.style.cssText = initial.embed
    ? 'display:flex;align-items:center;gap:0.75rem'
    : 'max-width:32rem;text-align:center;padding:2rem;display:flex;flex-direction:column;align-items:center;gap:1rem';
  inner.setAttribute('role', 'status');
  inner.setAttribute('aria-busy', 'true');
  inner.setAttribute('aria-live', 'polite');

  const iconEl = document.createElement('div');
  iconEl.setAttribute('aria-hidden', 'true');
  setHtmlIcon(iconEl, ICON_SPINNER);
  inner.appendChild(iconEl);

  const textWrap = document.createElement('div');
  textWrap.style.cssText = initial.embed
    ? 'display:flex;flex-direction:column;gap:0.125rem'
    : 'display:flex;flex-direction:column;gap:0.5rem;align-items:center';
  inner.appendChild(textWrap);

  const heading = document.createElement('p');
  heading.style.cssText = initial.embed ? 'font-weight:500' : 'font-size:1.125rem;font-weight:500';
  textWrap.appendChild(heading);

  const subtitle = document.createElement('p');
  subtitle.style.cssText = 'font-size:0.75rem;color:#a1a1aa';
  textWrap.appendChild(subtitle);

  const progressWrap = document.createElement('div');
  progressWrap.style.cssText = initial.embed
    ? 'width:8rem;height:0.25rem;background:rgba(255,255,255,0.1);border-radius:9999px;overflow:hidden'
    : 'width:20rem;height:0.5rem;background:rgba(255,255,255,0.1);border-radius:9999px;overflow:hidden';

  const progressBar = document.createElement('div');
  progressBar.style.cssText =
    'height:100%;background:#f4f4f5;border-radius:9999px;width:0%;transition:width 100ms linear';
  progressBar.setAttribute('role', 'progressbar');
  progressBar.setAttribute('aria-valuemin', '0');
  progressBar.setAttribute('aria-valuemax', '100');
  progressWrap.appendChild(progressBar);
  textWrap.appendChild(progressWrap);

  panel.appendChild(inner);
  root.appendChild(panel);

  const update = (props: LoadingProps): void => {
    setText(heading, t('viewer.loading.heading', { filename: props.filename }));
    const clientPart =
      props.clientName !== null
        ? t('viewer.loading.client_suffix', { client: props.clientName })
        : '';
    const sizePart = props.sizeBytes > 0 ? ` · ${formatBytes(props.sizeBytes)}` : '';
    setText(subtitle, `${formatPercent(props.modelProgress)}${clientPart}${sizePart}`);
    if (props.modelProgress > 0) {
      progressBar.style.width = formatPercent(props.modelProgress);
      progressBar.setAttribute('aria-valuenow', String(Math.round(props.modelProgress * 100)));
      progressBar.removeAttribute('aria-valuetext');
    } else {
      progressBar.style.width = '20%';
      progressBar.removeAttribute('aria-valuenow');
      progressBar.setAttribute('aria-valuetext', t('viewer.loading.aria_busy'));
    }
  };

  update(initial);

  return {
    update,
    cleanup() {
      panel.remove();
    },
  };
}
